import {css, StyleSheet} from 'aphrodite';
import 'moment-timezone';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import ModelHubExecutionCountNotice from 'model-hub/ModelHubExecutionCountNotice';
import {
  requireScientificModel,
  type ScientificModelNode,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import StructureViewerHeader from 'model-hub/scientific-models/structure-prediction/StructureViewerHeader';
import {useScientificModelRegistry} from 'model-hub/scientific-models/useAvailableScientificModels';
import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import {MolstarViewableExternalFile} from 'molbio/molstar/constants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  downloadStructureFile,
  findCoordinateStructureById,
  getStructuresForVersion,
  getStructureInfoFromVersions,
} from './AlphaFoldTabUtils';

export interface AlphaFoldOptionsProps {
  proteinId: string;
}

interface EnhancedAlphaFoldOptionsProps {
  structureFile: MolstarViewableExternalFile | null;
  model: string | null;
  modelVersion: string | null;
}

class AlphaFoldOptionsConnector extends DataConnector<AlphaFoldOptionsProps, EnhancedAlphaFoldOptionsProps> {
  override shouldSubscribeToRedux = false;
  override stores = [AaSequenceStore];

  // Track the last seen version ID to detect version changes
  private lastSeenVersionId: number | null = null;

  enhanceProps(): {
    structureFile: MolstarViewableExternalFile | null;
    model: string | null;
    modelVersion: string | null;
    proteinId: string;
    structureAaSequenceVersionId: number | null;
  } {
    const aaSequenceStore = this.aaSequenceStore();
    const aaSequence = aaSequenceStore.getAaSequenceById(this.props.proteinId)!;
    const proteinId = this.props.proteinId;
    const currentlyViewedAaSequenceVersionId = aaSequence.version.id;

    // Track if this is the first time we're seeing this version
    const versionChanged = this.lastSeenVersionId !== currentlyViewedAaSequenceVersionId;
    this.lastSeenVersionId = currentlyViewedAaSequenceVersionId;

    const versions = aaSequenceStore.getVersionsById(proteinId);
    const currentCoordinateStructureId = aaSequenceStore.getCoordinateStructureId(
      currentlyViewedAaSequenceVersionId
    );

    // If there's a current coordinate structure ID, use that specific prediction
    if (currentCoordinateStructureId) {
      const allCoordinateStructures = getStructuresForVersion(versions, currentlyViewedAaSequenceVersionId);
      const selectedCoordinateStructure = findCoordinateStructureById(
        allCoordinateStructures,
        currentCoordinateStructureId
      );

      if (selectedCoordinateStructure) {
        return {
          structureFile: selectedCoordinateStructure.structureFile,
          model: selectedCoordinateStructure.model,
          modelVersion: selectedCoordinateStructure.modelVersion,
          proteinId,
          structureAaSequenceVersionId: selectedCoordinateStructure.versionId,
        };
      }
    }

    // Fallback to getStructureInfoFromVersions when switching to a version for the first time
    // This handles initial load and version switching
    if (versionChanged) {
      const structureInfo = getStructureInfoFromVersions(currentlyViewedAaSequenceVersionId, versions);
      return {
        structureFile: structureInfo.structureFile,
        model: structureInfo.model,
        modelVersion: structureInfo.modelVersion,
        proteinId,
        structureAaSequenceVersionId: structureInfo.versionId,
      };
    }

    // No current prediction and we've seen this version before - return null values
    return {
      structureFile: null,
      model: null,
      modelVersion: null,
      proteinId,
      structureAaSequenceVersionId: null,
    };
  }
}

export function AlphaFoldOptions({
  model,
  ...props
}: {
  structureFile: MolstarViewableExternalFile | null;
  model: string | null;
  modelVersion: string | null;
  proteinId: string;
  structureAaSequenceVersionId: number | null;
}): JSX.Element {
  const result = useScientificModelRegistry();
  if (result.type === 'loading') {
    return <CenteredHorizontalThrobber />;
  }
  if (result.type === 'error') {
    return <div className={css(styles.optionButtonsLayout)} />;
  }
  return (
    <LoadedAlphaFoldOptions
      model={model == null ? null : requireScientificModel(result.registry.catalogByExternalReference, model)}
      {...props}
    />
  );
}

export function LoadedAlphaFoldOptions({
  structureFile,
  model,
  modelVersion,
  proteinId,
  structureAaSequenceVersionId,
}: {
  structureFile: MolstarViewableExternalFile | null;
  model: ScientificModelNode | null;
  modelVersion: string | null;
  proteinId: string;
  structureAaSequenceVersionId: number | null;
}): JSX.Element {
  if (
    structureFile != null &&
    model != null &&
    modelVersion != null &&
    structureAaSequenceVersionId != null
  ) {
    return (
      <StructureViewerHeader
        structureFile={structureFile}
        model={model}
        modelVersion={modelVersion}
        downloadStructureFile={() =>
          downloadStructureFile(structureFile, proteinId, structureAaSequenceVersionId)
        }
      />
    );
  } else if (BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC) {
    return <ModelHubExecutionCountNotice inBanner={false} />;
  } else {
    return <div className={css(styles.optionButtonsLayout)} />;
  }
}

const ConnectedAlphaFoldOptions = AlphaFoldOptionsConnector.connect(AlphaFoldOptions);
export default ConnectedAlphaFoldOptions;

const styles = StyleSheet.create({
  optionButtonsLayout: {
    padding: '0px 7px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    // Should match the value inherited from .buffer-options
    height: '35px',
  },
});
